/**
 * Ground Items — heal items dropped on player death.
 * Pure functions — no side effects, no rendering.
 */

import type { GameState, PlayerState, PlanetState, GroundItemState, ItemKey } from '../types';
import { GROUND_ITEM_PICKUP_RANGE } from '../constants';
import { vecNormalize, vecDot } from '../math';
import { dacos } from './dmath';

// =============================================================================
// HELPERS
// =============================================================================

function surfaceArcDistance(dir1: { x: number; y: number; z: number }, dir2: { x: number; y: number; z: number }, radius: number): number {
  const d1 = vecNormalize(dir1);
  const d2 = vecNormalize(dir2);
  const dot = Math.max(-1, Math.min(1, vecDot(d1, d2)));
  return dacos(dot) * radius;
}


// =============================================================================
// DROP ON DEATH
// =============================================================================

/**
 * When a player dies, spawn ground items for each heal in their loadout.items.
 * Returns updated state with new ground items and cleared heals from dead player.
 */
export function dropHealItemsOnDeath(
  state: GameState,
  playerId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const items = player.loadout?.items;
  if (!items) return state;

  // Ids from the match state, not a module counter that outlived its match (P2P checksum)
  let nextId = 1;
  for (const g of state.groundItems ?? []) if (g.id >= nextId) nextId = g.id + 1;

  const newGroundItems: GroundItemState[] = [];
  for (const key of Object.keys(items) as ItemKey[]) {
    const count = items[key];
    if (!count || count <= 0) continue;
    for (let i = 0; i < count; i++) {
      newGroundItems.push({
        id: nextId++,
        type: key,
        planetIndex: player.planetIndex,
        surfaceDir: { ...player.surfaceDir },
        alive: true,
      });
    }
  }

  if (newGroundItems.length === 0) return state;

  // Clear heals from dead player's loadout
  const updatedPlayers = state.players.map(p =>
    p.id === playerId
      ? { ...p, loadout: { ...p.loadout, items: {} } }
      : p
  );

  return {
    ...state,
    players: updatedPlayers,
    groundItems: [...state.groundItems, ...newGroundItems],
  };
}

// =============================================================================
// PICKUP
// =============================================================================

/**
 * Check if a player can pick up a specific ground item.
 */
export function canPickupGroundItem(
  player: PlayerState,
  item: GroundItemState,
  planets: PlanetState[]
): boolean {
  if (!player.alive || player.airborne) return false;
  if (!item.alive) return false;
  if (player.planetIndex !== item.planetIndex) return false;

  const planet = planets[player.planetIndex];
  const dist = surfaceArcDistance(player.surfaceDir, item.surfaceDir, planet.radius);
  return dist <= GROUND_ITEM_PICKUP_RANGE;
}

/**
 * Get all ground items a player can pick up.
 */
export function getPickupableGroundItems(
  player: PlayerState,
  groundItems: GroundItemState[],
  planets: PlanetState[]
): GroundItemState[] {
  return groundItems.filter(item => canPickupGroundItem(player, item, planets));
}

/**
 * Pick up a single ground item — marks it dead, adds to player's loadout.items.
 */
export function pickupGroundItem(
  state: GameState,
  playerId: number,
  itemId: number
): GameState {
  const item = state.groundItems.find(g => g.id === itemId && g.alive);
  if (!item) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  // Mark item as dead
  const updatedGroundItems = state.groundItems.map(g =>
    g.id === itemId ? { ...g, alive: false } : g
  );

  // Add to player's loadout.items
  const currentItems = player.loadout?.items ?? {};
  const currentCount = currentItems[item.type] ?? 0;
  const updatedItems = { ...currentItems, [item.type]: currentCount + 1 };
  const updatedPlayers = state.players.map(p =>
    p.id === playerId
      ? { ...p, loadout: { ...p.loadout, items: updatedItems } }
      : p
  );

  return {
    ...state,
    players: updatedPlayers,
    groundItems: updatedGroundItems,
  };
}

/**
 * Auto-pickup all nearby ground items for a player.
 * Returns updated state and list of picked up items (for logging).
 */
export function checkGroundItemPickup(
  state: GameState,
  playerId: number
): { state: GameState; pickedUp: GroundItemState[] } {
  const player = state.players.find(p => p.id === playerId);
  if (!player || !player.alive || player.airborne) return { state, pickedUp: [] };

  const nearby = getPickupableGroundItems(player, state.groundItems, state.planets);
  if (nearby.length === 0) return { state, pickedUp: [] };

  let current = state;
  for (const item of nearby) {
    current = pickupGroundItem(current, playerId, item.id);
  }

  return { state: current, pickedUp: nearby };
}
